import React, { useState, useEffect } from 'react';
import axios from 'axios';

const IPManager = () => {
  const [conflicts, setConflicts] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [ipHistory, setIpHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('conflicts'); // 'conflicts' or 'users'

  useEffect(() => {
    if (activeTab === 'conflicts') {
      fetchIPConflicts();
    } else {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchIPConflicts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/ip-conflicts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConflicts(response.data.conflicts);
    } catch (error) {
      console.error('Error fetching IP conflicts:', error);
      alert('IP çakışmaları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/users?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      alert('Kullanıcılar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserIPHistory = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/users/${userId}/ip-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIpHistory(response.data.history);
      setSelectedUser(userId);
    } catch (error) {
      console.error('Error fetching IP history:', error);
      alert('IP geçmişi yüklenirken bir hata oluştu');
    }
  };

  const getConflictSeverity = (userCount) => {
    if (userCount >= 5) return 'high';
    if (userCount >= 3) return 'medium';
    return 'low';
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/20 border-red-500 text-red-400';
      case 'medium':
        return 'bg-yellow-500/20 border-yellow-500 text-yellow-400';
      case 'low':
        return 'bg-blue-500/20 border-blue-500 text-blue-400';
      default:
        return 'bg-gray-500/20 border-gray-500 text-gray-400';
    }
  };

  return (
    <div className="bg-gradient-card rounded-xl p-6 border border-gold/30">
      <h2 className="text-2xl font-bold text-gold mb-6">IP Yönetimi</h2>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-gold/20">
        <button
          onClick={() => setActiveTab('conflicts')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'conflicts'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          IP Çakışmaları
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'users'
              ? 'text-gold border-b-2 border-gold'
              : 'text-white/60 hover:text-gold'
          }`}
        >
          Tüm Kullanıcılar
        </button>
      </div>

      {/* IP Conflicts Tab */}
      {activeTab === 'conflicts' && (
        <div>
          {loading ? (
            <div className="text-center text-white py-8">Yükleniyor...</div>
          ) : conflicts.length === 0 ? (
            <div className="text-center text-white py-8 bg-navy-800/50 rounded-lg">
              IP çakışması bulunamadı. Tüm kullanıcılar farklı IP adreslerine sahip.
            </div>
          ) : (
            <div className="space-y-4">
              {conflicts.map((conflict, index) => {
                const severity = getConflictSeverity(conflict.userCount);
                return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${getSeverityColor(severity)}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="font-bold text-lg">IP: {conflict.ipAddress}</span>
                        <span className="ml-3 px-3 py-1 rounded-full bg-white/10 text-sm">
                          {conflict.userCount} Kullanıcı
                        </span>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        severity === 'high' ? 'bg-red-500 text-white' :
                        severity === 'medium' ? 'bg-yellow-500 text-navy-900' :
                        'bg-blue-500 text-white'
                      }`}>
                        {severity === 'high' ? 'Yüksek Risk' :
                         severity === 'medium' ? 'Orta Risk' : 'Düşük Risk'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {conflict.users.map((user) => (
                        <div
                          key={user.id}
                          className="bg-black/20 p-3 rounded-lg flex items-center justify-between"
                        >
                          <div>
                            <span className="font-semibold text-white">{user.username}</span>
                            <span className="text-white/60 ml-3">({user.email})</span>
                            <span className="text-white/60 ml-3">
                              @{user.telegramUsername}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-white/60 text-sm">
                              {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                            <button
                              onClick={() => fetchUserIPHistory(user.id)}
                              className="px-3 py-1 bg-gold/20 hover:bg-gold/30 text-gold rounded text-sm transition-colors"
                            >
                              IP Geçmişi
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          {loading ? (
            <div className="text-center text-white py-8">Yükleniyor...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gold/30">
                    <th className="px-4 py-3 text-gold font-semibold">ID</th>
                    <th className="px-4 py-3 text-gold font-semibold">Kullanıcı Adı</th>
                    <th className="px-4 py-3 text-gold font-semibold">E-posta</th>
                    <th className="px-4 py-3 text-gold font-semibold">IP Adresi</th>
                    <th className="px-4 py-3 text-gold font-semibold">Kayıt Tarihi</th>
                    <th className="px-4 py-3 text-gold font-semibold">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-gold/10 hover:bg-navy-800/50">
                      <td className="px-4 py-3 text-white">{user.id}</td>
                      <td className="px-4 py-3 text-white">{user.username}</td>
                      <td className="px-4 py-3 text-white">{user.email}</td>
                      <td className="px-4 py-3 text-white font-mono text-sm">
                        {user.ipAddress || '-'}
                      </td>
                      <td className="px-4 py-3 text-white/80 text-sm">
                        {new Date(user.createdAt).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => fetchUserIPHistory(user.id)}
                          className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-4 py-1 rounded-lg transition-all duration-300 text-sm"
                        >
                          IP Geçmişi
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* IP History Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-card rounded-xl p-6 border border-gold/30 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gold">IP Geçmişi</h3>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setIpHistory([]);
                }}
                className="text-white hover:text-gold text-2xl"
              >
                ×
              </button>
            </div>
            {ipHistory.length === 0 ? (
              <div className="text-center text-white py-8">IP geçmişi bulunamadı</div>
            ) : (
              <div className="space-y-2">
                {ipHistory.map((entry, index) => (
                  <div
                    key={index}
                    className="bg-navy-800 p-4 rounded-lg border border-gold/20"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-mono text-gold font-semibold">{entry.ipAddress || entry.ip_address}</span>
                        <span className="ml-3 px-2 py-1 bg-gold/20 text-gold rounded text-sm">
                          {entry.action === 'register' ? 'Kayıt' : 'Giriş'}
                        </span>
                      </div>
                      <span className="text-white/60 text-sm">
                        {new Date(entry.createdAt || entry.created_at).toLocaleString('tr-TR')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IPManager;
